import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export function NewAuctionForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    aktenzeichen: '',
    titel: '',
    patent_nummer: '',
    anmeldetag: '',
    erteilungstag: '',
    ablaufdatum: '',
    schutzlaender: 'DE,EP,US',
    ipc_klasse: '',
    aktueller_inhaber: '',
    jahresgebuehren_bis: new Date().getFullYear().toString(),
    technische_zusammenfassung: '',
    strategicSections: [{ section_id: '', titel: '', body: '' }],
    mindestgebot: '',
    mindest_schrittweite: '',
    reservepreis: '',
    start_at: '',
    end_at: '',
    compliance_frist_tage: '10',
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function setField(key: string, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const strategic = form.strategicSections
      .filter((s) => s.titel && s.body)
      .map((s) => ({
        section_id: s.section_id || s.titel.toLowerCase().replace(/\s+/g, '-'),
        titel: s.titel,
        body: s.body,
      }));

    const { data: auction, error: insertError } = await supabase
      .from('auctions')
      .insert({
        aktenzeichen: form.aktenzeichen.trim(),
        titel: form.titel.trim(),
        patent_nummer: form.patent_nummer.trim(),
        anmeldetag: form.anmeldetag,
        erteilungstag: form.erteilungstag,
        ablaufdatum: form.ablaufdatum,
        schutzlaender: form.schutzlaender.split(',').map((s) => s.trim()).filter(Boolean),
        ipc_klasse: form.ipc_klasse.trim(),
        aktueller_inhaber: form.aktueller_inhaber.trim(),
        jahresgebuehren_bis: form.jahresgebuehren_bis.trim(),
        technische_zusammenfassung: form.technische_zusammenfassung.trim(),
 strategische_einordnung: strategic,
        mindestgebot: parseFloat(form.mindestgebot),
        mindest_schrittweite: parseFloat(form.mindest_schrittweite),
        reservepreis: form.reservepreis ? parseFloat(form.reservepreis) : null,
        start_at: form.start_at || new Date().toISOString(),
        end_at: form.end_at,
        compliance_frist_tage: parseInt(form.compliance_frist_tage),
      })
      .select()
      .single();

    if (insertError) {
      setError(insertError.message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    navigate(`/admin/auktionen/${auction.aktenzeichen}`);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-surface p-6 rounded-sm max-w-2xl w-full mx-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-serif mb-4">Neue Auktion erstellen</h2>

        {error && (
          <div className="mb-4 p-3 bg-status-danger/10 border border-status-danger/20 text-status-danger text-sm rounded-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <fieldset className="border border-surface-border p-4">
            <legend className="font-serif text-sm font-semibold">Identifikation</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium mb-1">Aktenzeichen *</label>
                <input className="input-field" value={form.aktenzeichen} onChange={(e) => setField('aktenzeichen', e.target.value)} placeholder="AUKT-2026-0001" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Patent-Kurztitel *</label>
                <input className="input-field" value={form.titel} onChange={(e) => setField('titel', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Patentnummer *</label>
                <input className="input-field" value={form.patent_nummer} onChange={(e) => setField('patent_nummer', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">IPC-Klasse *</label>
                <input className="input-field" value={form.ipc_klasse} onChange={(e) => setField('ipc_klasse', e.target.value)} />
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-surface-border p-4">
            <legend className="font-serif text-sm font-semibold">Daten</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium mb-1">Anmeldetag *</label>
                <input type="date" className="input-field" value={form.anmeldetag} onChange={(e) => setField('anmeldetag', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Erteilungstag *</label>
                <input type="date" className="input-field" value={form.erteilungstag} onChange={(e) => setField('erteilungstag', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ablaufdatum *</label>
                <input type="date" className="input-field" value={form.ablaufdatum} onChange={(e) => setField('ablaufdatum', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Schutzländer</label>
                <input className="input-field" value={form.schutzlaender} onChange={(e) => setField('schutzlaender', e.target.value)} placeholder="DE,EP,US" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Aktueller Inhaber</label>
                <input className="input-field" value={form.aktueller_inhaber} onChange={(e) => setField('aktueller_inhaber', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Jahresgebühren bis</label>
                <input className="input-field" value={form.jahresgebuehren_bis} onChange={(e) => setField('jahresgebuehren_bis', e.target.value)} />
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-surface-border p-4">
            <legend className="font-serif text-sm font-semibold">Technische Zusammenfassung</legend>
            <textarea className="input-field mt-3" rows={4} value={form.technische_zusammenfassung} onChange={(e) => setField('technische_zusammenfassung', e.target.value)} placeholder="Markdown-Text..." />
          </fieldset>

          <fieldset className="border border-surface-border p-4">
            <legend className="font-serif text-sm font-semibold">Gebotsparameter</legend>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium mb-1">Mindestgebot *</label>
                <input type="number" className="input-field" value={form.mindestgebot} onChange={(e) => setField('mindestgebot', e.target.value)} step="0.01" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mindestschrittweite *</label>
                <input type="number" className="input-field" value={form.mindest_schrittweite} onChange={(e) => setField('mindest_schrittweite', e.target.value)} step="0.01" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reservepreis (optional)</label>
                <input type="number" className="input-field" value={form.reservepreis} onChange={(e) => setField('reservepreis', e.target.value)} step="0.01" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Compliance-Frist (Tage)</label>
                <input type="number" className="input-field" value={form.compliance_frist_tage} onChange={(e) => setField('compliance_frist_tage', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium mb-1">Start-Zeitpunkt *</label>
                <input type="datetime-local" className="input-field" value={form.start_at} onChange={(e) => setField('start_at', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ende-Zeitpunkt *</label>
                <input type="datetime-local" className="input-field" value={form.end_at} onChange={(e) => setField('end_at', e.target.value)} />
              </div>
            </div>
          </fieldset>

          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Erstelle...' : 'Auktion erstellen'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Abbrechen</button>
          </div>
        </form>
      </div>
    </div>
  );
}
